import * as React from "react";
import { useIntl } from "@grandvisionhq/state";
import { getWeekDaysForWeeksFromDate, isDateBefore, shiftDate, isSameDay } from "../../../../../utils/v3/utils/datetime.js";
import AvailabilityCalendar from "./AvailabilityCalendar.js";
import { useRangeByWeeks } from "../../../hooks/v3/components/AvailabilityCalendar/hooks/use-range-by-weeks.js";
import { tileNameLabelsByWeeks, formatTileData, getLastWeekDay } from "./utils.js";
const AvailabilityCalendarContainer = ({
  selectedDate: _selectedDate,
  weekDate: _weekDate,
  weekCount = 2,
  closedDates,
  minEnabledDate: _minEnabledDate,
  maxEnabledDate,
  availability = {},
  loading = false,
  noTimeSlots,
  onSelectDate,
  onChangeWeeks,
  ...rest
}) => {
  const weekStartOffset = 1;
  const { getLabel } = useIntl();
  const today = React.useRef(/* @__PURE__ */ new Date());
  const [weekDate, setWeekDate] = React.useState(_weekDate ?? today.current);
  const [weeks, setWeeks] = React.useState(
    getWeekDaysForWeeksFromDate(weekDate, weekCount, weekStartOffset)
  );
  const [selectedDate, setSelectedDate] = React.useState(_selectedDate);
  const minEnabledDate = _minEnabledDate ?? today.current;
  const labels = React.useMemo(
    () => ({
      pastButton: getLabel("appointments.component.availabilityCalendar.ariaLabels.pastButton"),
      futureButton: getLabel("appointments.component.availabilityCalendar.ariaLabels.futureButton"),
      legend: getLabel("appointments.component.availabilityCalendar.legend")
    }),
    [getLabel]
  );
  const range = useRangeByWeeks(weeks);
  const tileNames = React.useMemo(
    () => tileNameLabelsByWeeks(weeks)?.map((label) => getLabel(label)),
    [getLabel, weeks]
  );
  const tiles = React.useMemo(
    () => formatTileData(
      weeks,
      availability,
      closedDates,
      minEnabledDate,
      maxEnabledDate,
      selectedDate
    ),
    [weeks, availability, minEnabledDate, maxEnabledDate, selectedDate, closedDates]
  );
  const firstWeekDay = weeks[0]?.[0];
  const lastWeek = weeks[weeks.length - 1];
  const lastWeekDay = lastWeek?.[lastWeek.length - 1];
  const showPastButton = firstWeekDay ? isDateBefore(minEnabledDate, shiftDate(firstWeekDay, "day", -1)) : false;
  const showFutureButton = lastWeekDay && maxEnabledDate ? isDateBefore(shiftDate(lastWeekDay, "day", 1), maxEnabledDate) : true;
  const onClickPastButton = React.useCallback(() => {
    const newWeekDate = shiftDate(weekDate ?? today.current, "week", -weekCount);
    setWeekDate(newWeekDate);
    const newWeeks = getWeekDaysForWeeksFromDate(newWeekDate, weekCount, weekStartOffset);
    setWeeks(newWeeks);
    onChangeWeeks && onChangeWeeks(newWeeks);
  }, [weekDate, setWeekDate, weekCount, weekStartOffset, onChangeWeeks]);
  const onClickFutureButton = React.useCallback(() => {
    const newWeekDate = shiftDate(weekDate ?? today.current, "week", weekCount);
    setWeekDate(newWeekDate);
    const newWeeks = getWeekDaysForWeeksFromDate(newWeekDate, weekCount, weekStartOffset);
    setWeeks(newWeeks);
    onChangeWeeks && onChangeWeeks(newWeeks);
  }, [weekDate, setWeekDate, weekCount, weekStartOffset, onChangeWeeks]);
  const onClickTile = React.useCallback(
    (date) => {
      setSelectedDate(date);
      onSelectDate && onSelectDate(date);
    },
    [onSelectDate]
  );
  React.useEffect(() => setSelectedDate(_selectedDate), [_selectedDate]);
  React.useEffect(() => {
    const newWeekDate = _weekDate ?? today.current;
    setWeekDate(_weekDate ?? today.current);
    const newWeeks = getWeekDaysForWeeksFromDate(newWeekDate, weekCount, weekStartOffset);
    setWeeks(newWeeks);
    const currFirstDay = weeks[0]?.[0];
    const currLastDay = getLastWeekDay(weeks);
    const newFirstDay = newWeeks[0]?.[0];
    const newLastDay = getLastWeekDay(newWeeks);
    if (currFirstDay && currLastDay && newFirstDay && newLastDay) {
      if (isSameDay(currFirstDay, newFirstDay) && isSameDay(currLastDay, newLastDay)) {
        return;
      }
    } else {
      console.error("Missing required data for weeks or newWeeks");
    }
    onChangeWeeks && onChangeWeeks(newWeeks);
  }, [_weekDate, weekCount]);
  React.useEffect(() => onChangeWeeks && onChangeWeeks(weeks), []);
  return /* @__PURE__ */ React.createElement(
    AvailabilityCalendar,
    {
      ...rest,
      labels,
      range,
      showPastButton,
      showFutureButton,
      onClickPastButton,
      onClickFutureButton,
      onClickTile,
      tileNames,
      tiles,
      loading,
      noTimeSlots
    }
  );
};
export {
  AvailabilityCalendarContainer as default
};
//# sourceMappingURL=AvailabilityCalendarContainer.js.map
